<template>
<LayoutSearchFieldVertical>
  <div class="search-field-temporal-group">
      <span class="year-label">{{
        lss('西暦で指定', 'Year Range')
      }}</span>

      <form
        class="search-field-time-range-form"
        @submit.prevent="addRange"
      >
        <div
          class="search-field-time-range-row"
        >
          <span
            class="time-range-heading"
          >{{ lss('開始日', 'Start date') }}</span>

          <div
            class="search-field-time-range-row-input"
          >
            <SearchFieldInputWithLabel
              :label="lss('年', 'Year')"
              :id="`temporal-group-start-date-${field.key}-year`"
            >
              <input
                class="jps-input jps-keyword-input time-range-input-year"
                type="number"
                min="0"
                :id="`temporal-group-start-date-${field.key}-year`"
                v-model="yearFrom"
                @keyup.prevent.esc="yearFrom = null"
              />
            </SearchFieldInputWithLabel>

            <SearchFieldInputWithLabel
              :label="lss('月', 'Month')"
              :id="`temporal-group-start-date-${field.key}-month`"
            >
              <input
                class="jps-input jps-keyword-input time-range-input-month"
                type="number"
                min="0"
                :id="`temporal-group-start-date-${field.key}-month`"
                v-model="monthFrom"
                @keyup.prevent.esc="monthFrom = null"
              />
            </SearchFieldInputWithLabel>

            <SearchFieldInputWithLabel
              :label="lss('日', 'Date')"
              :id="`temporal-group-start-date-${field.key}-date`"
            >
              <input
                class="jps-input jps-keyword-input time-range-input-month"
                type="number"
                min="0"
                :id="`temporal-group-start-date-${field.key}-date`"
                v-model="dayFrom"
                @keyup.prevent.esc="dayFrom = null"
              />
            </SearchFieldInputWithLabel>
          </div>
        </div>

        <div
          class="search-field-time-range-row"
        >
          <span
            class="time-range-heading"
          >{{ lss('終了日', 'End date') }}</span>

          <div
            class="search-field-time-range-row-input"
          >
            <SearchFieldInputWithLabel
              :label="lss('年', 'Year')"
              :id="`temporal-group-end-date-${field.key}-year`"
            >
              <input
                class="jps-input jps-keyword-input time-range-input-year"
                type="number"
                min="0"
                v-model="yearTo"
                @keyup.prevent.esc="yearTo = null"
              />
            </SearchFieldInputWithLabel>

            <SearchFieldInputWithLabel
              :label="lss('月', 'Month')"
              :id="`temporal-group-end-date-${field.key}-month`"
            >
              <input
                class="jps-input jps-keyword-input time-range-input-month"
                type="number"
                min="0"
                v-model="monthTo"
                @keyup.prevent.esc="monthTo = null"
              />
            </SearchFieldInputWithLabel>

            <SearchFieldInputWithLabel
              :label="lss('日', 'Date')"
              :id="`temporal-group-end-date-${field.key}-date`"
            >
              <input
                class="jps-input jps-keyword-input time-range-input-month"
                type="number"
                min="0"
                v-model="dayTo"
                @keyup.prevent.esc="dayTo = null"
              />
            </SearchFieldInputWithLabel>
          </div>
        </div>

        <div
          class="time-range-nav"
        >
          <UiButtonIcon
            icon-size="size-5"
            styled="secondary"
            tag="button"
            type="submit"
            icon="mdi/plus-circle"
          />
        </div>
      </form>
    <div v-if="field.samples" class="sample-query">
      <dl>
        <dt class="jps-flex-center">
          {{ ls({ ja: '例', en: 'Example ' }) }}
        </dt>
        <dd v-for="keyword in field.samples" :key="keyword.ja">
          <a class="jps-text-button" @click="addKeyword(keyword.ja)">
            {{ ls(keyword) }}
          </a>
        </dd>
      </dl>
    </div>
  </div>
</LayoutSearchFieldVertical>
</template>

<script lang="ts" setup>
import type { SearchFieldBaseProps } from '~/components/search/search-field/useBaseSearchField'

const { ls, lss } = useI18n()

interface Props extends SearchFieldBaseProps {
}
const props = withDefaults(defineProps<Props>(), {
})

const yearFrom: Ref<number> = ref(null)
const monthFrom: Ref<number> = ref(null)
const dayFrom: Ref<number> = ref(null)
const yearTo: Ref<number> = ref(null)
const monthTo: Ref<number> = ref(null)
const dayTo: Ref<number> = ref(null)

const addRange = () => {
  if (yearFrom.value == null && yearTo.value == null) return
  let year = {}
  // 入力状態の不正チェック
  const from = dateValue(yearFrom.value, monthFrom.value, dayFrom.value, true)
  const to = dateValue(yearTo.value, monthTo.value, dayTo.value, false)
  if (from) year['from'] = '' + from
  if (to) year['to'] = '' + to
  props.ss.state.range[props.field.key] = [year]
  props.ss.execute()
}

const dateValue = (year: number, month: number, day: number, isFrom: boolean): string => {
  if (!year) return null // 年の指定がないのは不正
  if (year && !month && day) return null // 年と日の指定があるのに月の指定がないのは不正
  if (isFrom) {
    return ('' + year).padStart(4, '0')
    + (month ? ('' + month).padStart(2, '0') : '01')
    + (day ? ('' + day).padStart(2, '0') : '01')
  } else {
    const y = ('' + year).padStart(4, '0')
    const m = month ? ('' + month).padStart(2, '0') : '12'
    let d = day ? ('' + day).padStart(2, '0') : null
    if (!day) {
      try {
        const dt = new Date(y, m, 0)
        d = dt.getDate()
      } catch (e){
        return null
      }
    }
    return y + m + d
  }
}

const addKeyword = (word: string) => {
  props.ss.addQuery(props.field.key, [word])
  value.value = null
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.search-field-temporal-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.search-field-time-range {
  display: flex;
  gap: 0.25rem 0.75rem;
  flex-wrap: wrap;
  align-items: end;
}

.search-field-time-range-row {
  display: flex;
  gap: 0.25rem 0.5rem;
  flex-wrap: wrap;
  align-items: end;

  .time-range-input-year {
    max-width: 5rem;
    text-align: right;
  }
  .time-range-input-month {
    max-width: 4rem;
    text-align: right;
  }
}

.search-field-time-range-row-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-field-time-range-form {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: end;
}

.time-range-select {
  line-height: 1.8;
  margin: 0;
}

.time-range-heading,
.time-range-nav {
  min-height: 2.125rem;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
